import React, { useEffect, useState } from 'react';
import { inviteService } from '../../services/AssessmentService';
import { useInviteTracking } from '../../hooks/useInviteTracking';

/**
 * What a candidate sees at ?page=fragebogen2&id=…&t=…
 *
 * The questionnaire itself is a self-contained document: it carries its own
 * styles and scripts, keeps progress in localStorage and submits by mail. It is
 * handed over whole rather than rebuilt here, so this page only checks the
 * token, reports progress back to the invitation and otherwise gets out of the way.
 */
export function PublicAssessmentPage({ assessmentId, token }) {
  const [state, setState] = useState({ status: 'checking' });
  // Starts only once the document is in hand, so "geöffnet" means the
  // candidate actually saw the questionnaire.
  const { onFrameLoad, submitState, sendByMail, backToQuestionnaire } = useInviteTracking(
    state.status === 'ready' ? state.invite : null
  );

  // Candidates see this tab, so it names what they opened rather than the
  // admin app that happens to serve it. Restored for the SPA's other pages.
  useEffect(() => {
    const previous = document.title;
    document.title = 'Fragebogen';
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    let active = true;

    (async () => {
      const result = await inviteService.getByToken(token);
      if (!active) return;

      if (!result.success) {
        setState({ status: 'invalid', message: result.error });
        return;
      }

      const invite = result.data;
      if (assessmentId && String(invite.assessmentId) !== String(assessmentId)) {
        setState({ status: 'invalid', message: 'Dieser Link gehört nicht zu diesem Fragebogen.' });
        return;
      }
      if (!invite.documentUrl) {
        setState({ status: 'invalid', message: 'Zu diesem Link ist kein Fragebogen hinterlegt.' });
        return;
      }

      // Fetch the document and hand it to the frame as srcdoc rather than
      // pointing the frame at PocketBase. A cross-origin frame is third-party
      // storage: Safari blocks it outright and Chrome partitions it, and this
      // questionnaire keeps the candidate's progress in localStorage. Injected
      // this way it inherits this page's origin, so saving, the PDF export and
      // the mailto submit all behave as they do when the file is opened
      // straight off the desktop.
      try {
        const response = await fetch(invite.documentUrl);
        if (!response.ok) throw new Error(String(response.status));
        const html = await response.text();
        if (!active) return;
        setState({ status: 'ready', html, invite });
      } catch {
        if (!active) return;
        setState({ status: 'invalid', message: 'Der Fragebogen konnte nicht geladen werden.' });
      }
    })();

    return () => {
      active = false;
    };
  }, [assessmentId, token]);

  if (state.status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto" />
          <p className="mt-4 text-slate-600">Fragebogen wird geladen …</p>
        </div>
      </div>
    );
  }

  if (state.status === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-lg">
          <h1 className="text-xl font-bold text-slate-800">Fragebogen nicht verfügbar</h1>
          <p className="mt-2 text-slate-600">{state.message}</p>
          <p className="mt-4 text-sm text-slate-500">
            Schreib uns einfach eine kurze Nachricht, dann helfen wir dir weiter.
          </p>
        </div>
      </div>
    );
  }

  // A frame, because the questionnaire is a whole page with its own <head> and
  // styles that would otherwise collide with this app's. No sandbox attribute:
  // the document is ours, and every restriction it could add is one the
  // questionnaire needs. allow-same-origin for localStorage, allow-downloads
  // for the PDF export, allow-scripts for all of it.
  return (
    <>
      <iframe
        srcDoc={state.html}
        title="Fragebogen 2"
        allow="clipboard-write"
        onLoad={onFrameLoad}
        className="fixed inset-0 h-full w-full border-0"
      />
      {/* Die Abgabe gehört uns, nicht dem Mailprogramm: der Klick im Dokument
          wird abgefangen, hier steht, was daraus geworden ist. Ohne das sieht
          ein Bewerber nur einen Mail-Entwurf und weiß nicht, ob er fertig ist. */}
      {submitState && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 px-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl">
            {submitState === 'sending' ? (
              <>
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto" />
                <p className="mt-4 text-slate-600">Deine Antworten werden übermittelt …</p>
              </>
            ) : (
              <>
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h1 className="mt-4 text-2xl font-bold text-slate-800">Angekommen!</h1>
                <p className="mt-2 text-slate-600">
                  Deine Antworten liegen jetzt bei uns — du musst nichts weiter tun. Wir melden uns bei dir.
                </p>
                <button
                  type="button"
                  onClick={backToQuestionnaire}
                  className="mt-6 w-full rounded-xl bg-slate-800 px-5 py-3 font-semibold text-white transition-colors hover:bg-slate-900"
                >
                  Zurück zum Fragebogen
                </button>
                <button
                  type="button"
                  onClick={sendByMail}
                  className="mt-3 text-sm text-slate-500 underline transition-colors hover:text-slate-700"
                >
                  Antworten zusätzlich per E-Mail schicken
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
